import PropTypes from 'prop-types';
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';

const digits = (value) => String(value ?? '').replace(/\D/g, '');
const clean = (value) => String(value ?? '').trim();

const abhaDob = (profile) => {
  if (profile?.dateOfBirth?.format) return profile.dateOfBirth.format('YYYY-MM-DD');
  if (profile?.yearOfBirth) {
    const pad = (n) => String(n || '01').padStart(2, '0');
    return `${profile.yearOfBirth}-${pad(profile.monthOfBirth)}-${pad(profile.dayOfBirth)}`;
  }
  return clean(profile?.dob);
};

const joinAddress = (...parts) => parts.map(clean).filter(Boolean).join(', ');

/** The rows shown for each side: [label, HMS value, ABHA value, how to compare]. */
export const comparisonRows = (patient, profile) => [
  ['Name', patient?.full_name, [profile?.firstName, profile?.middleName, profile?.lastName].filter(Boolean).join(' '), 'text'],
  ['Gender', patient?.gender, profile?.gender, 'text'],
  ['DOB', patient?.dob, abhaDob(profile), 'text'],
  ['Mobile', patient?.phone_number, profile?.mobile, 'digits'],
  ['ABHA address', patient?.abha_address, profile?.abhaAddress, 'text'],
  [
    'Address',
    joinAddress(
      patient?.address ?? patient?.metadata?.address,
      patient?.state ?? patient?.metadata?.state,
      patient?.home_pincode
    ),
    joinAddress(profile?.address, profile?.state, profile?.pincode),
    'text'
  ]
];

const differs = (hms, abha, how) => {
  if (how === 'digits') return digits(hms).slice(-10) !== digits(abha).slice(-10);
  return clean(hms).toLowerCase() !== clean(abha).toLowerCase();
};

function RecordCard({ title, subtitle, rows, side, highlight }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', ...(highlight && { borderColor: 'success.light', bgcolor: 'success.lighter' }) }}>
      <Typography variant="subtitle1">{title}</Typography>
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
      <Stack spacing={1.25} sx={{ mt: 2 }}>
        {rows.map(([label, hms, abha, how]) => {
          const value = side === 'hms' ? hms : abha;
          const changed = differs(hms, abha, how);
          return (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: changed ? 600 : 400, color: changed ? 'warning.dark' : 'text.primary' }}>
                {clean(value) || '—'}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

RecordCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  rows: PropTypes.array.isRequired,
  side: PropTypes.oneOf(['hms', 'abha']).isRequired,
  highlight: PropTypes.bool
};

/**
 * M1 iteration 2 obs 8: the verified ABHA belongs to a patient already in
 * HMS - show both records side by side and let the user keep one set of
 * details. Differing fields are highlighted.
 */
export default function AbhaComparison({ patient, profile, onGoWithAbdm, onGoWithHims, disabled }) {
  const rows = comparisonRows(patient, profile);
  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        This ABHA belongs to a patient already registered in HMS{patient?.id ? ` (UHID ${patient.id})` : ''}. Highlighted
        fields differ - choose which details to keep.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <RecordCard title="Current Record" subtitle="In HMS" rows={rows} side="hms" />
        </Grid>
        <Grid item xs={12} md={6}>
          <RecordCard title="ABHA Data" subtitle="From Aadhaar / ABHA" rows={rows} side="abha" highlight />
        </Grid>
      </Grid>
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={onGoWithHims} disabled={disabled}>
          Go with HIMS
        </Button>
        <Button variant="contained" onClick={onGoWithAbdm} disabled={disabled}>
          Go with ABDM
        </Button>
      </Stack>
    </Stack>
  );
}

AbhaComparison.propTypes = {
  patient: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  onGoWithAbdm: PropTypes.func.isRequired,
  onGoWithHims: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};
